import { useState } from "react";
import { CheckCircle2, Copy, Loader2, PlugZap, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/crm/Badges";
import { ContactBackfillPanel } from "@/components/crm/ghl/ContactBackfillPanel";
import { PlatformUsagePanel } from "@/components/crm/ghl/PlatformUsagePanel";
import { brand } from "@/lib/brand";

const LABELS = { api_key: "Token (Private Integration)", location_id: "Location ID", client_id: "Client ID (app)", client_secret: "Client Secret (app)", webhook_key: "Clave de firma webhooks" };

const CopyRow = ({ label, value, testid }) => (
  <div className="mt-3">
    <p className="text-xs font-semibold text-slate-500">{label}</p>
    <div className="mt-1 flex items-center gap-2">
      <code className="min-w-0 flex-1 break-all rounded-xl bg-[#0B2545] p-2.5 text-xs text-white" data-testid={testid}>{value}</code>
      <Button variant="outline" size="icon" className="h-9 w-9 shrink-0 rounded-xl" aria-label="Copiar" data-testid={`${testid}-copy`}
              onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copiado"); }}><Copy className="h-4 w-4" /></Button>
    </div>
  </div>
);

export const GhlStatusPanel = ({ status, onChange }) => {
  const [testing, setTesting] = useState(false);
  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/integrations/ghl/test");
      onChange(data);
      data.state === "Conectado" ? toast.success(`Conectado a Plataforma de mensajería: ${data.location_name}`) : toast.error(`No se pudo conectar con la plataforma: ${brand(data.reason)}`);
    } catch (e) { toast.error(errorMessage(e)); } finally { setTesting(false); }
  };
  const ok = status.state === "Conectado";
  return (
    <div className="grid gap-6 xl:grid-cols-3">
      <Panel title="Conexión" className="xl:col-span-2" testid="ghl-status-panel">
        <div className={`flex items-start gap-3 rounded-xl p-4 ${ok ? "bg-emerald-50" : "bg-[#F5F7FA]"}`}>
          {ok ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <XCircle className="h-5 w-5 text-slate-400" />}
          <div className="min-w-0 text-sm" data-testid="ghl-connection-message">
            {ok ? <p className="font-semibold text-emerald-800">Conectado a Plataforma de mensajería · {status.location_name}</p>
              : status.state === "Error" ? <p className="font-semibold text-rose-700">No se pudo conectar con la plataforma: {brand(status.reason)}</p>
                : <p className="font-semibold">{status.state}</p>}
            {status.checked_at && <p className="mt-0.5 text-xs text-slate-500">Última verificación: {fmtDateTime(status.checked_at)}</p>}
          </div>
        </div>
        <Button onClick={test} disabled={testing} className="mt-4 rounded-xl bg-[#1E6FD9]" data-testid="ghl-test-button">
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PlugZap className="mr-1.5 h-4 w-4" /> Probar conexión</>}
        </Button>
        <dl className="mt-6 divide-y divide-slate-100 text-sm" data-testid="ghl-credentials">
          {Object.entries(status.credentials).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 py-2.5">
              <dt className="text-slate-500">{LABELS[k]}</dt>
              <dd className={`text-right font-medium ${v.startsWith("no") ? "text-amber-600" : ""}`} data-testid={`ghl-cred-${k}`}>{v}</dd>
            </div>
          ))}
          <div className="flex justify-between gap-4 py-2.5">
            <dt className="text-slate-500">App Marketplace</dt>
            <dd className="text-right font-medium" data-testid="ghl-app-installed">{status.app.installed ? `Instalada · ${fmtDateTime(status.app.installed_at)}` : status.app.uninstalled_at ? `Desinstalada · ${fmtDateTime(status.app.uninstalled_at)}` : "No instalada"}</dd>
          </div>
          <div className="flex justify-between gap-4 py-2.5">
            <dt className="text-slate-500">Canales conectados en la plataforma</dt>
            <dd className="text-right font-medium" data-testid="ghl-channels">
              {!status.channels ? <span className="text-slate-400">Pulsa “Probar conexión”</span> : (
                <>
                  <span className="block">Teléfono de la subcuenta: {status.channels.location_phone || "—"}</span>
                  <span className="block text-xs font-normal text-slate-500">
                    Números LC Phone: {status.channels.phone_numbers_available ? (status.channels.phone_numbers.join(", ") || "ninguno") : "no disponible"}
                  </span>
                  <span className="block text-xs font-normal text-slate-400">El número de WhatsApp no lo expone la plataforma</span>
                </>
              )}
            </dd>
          </div>
        </dl>
      </Panel>
      <Panel title="URLs para la app de la plataforma">
        <CopyRow label="Webhook URL" value={status.webhook_url} testid="ghl-webhook-url" />
        <CopyRow label="OAuth Redirect URL" value={status.oauth_redirect_url} testid="ghl-oauth-url" />
        <p className="mt-4 text-xs text-slate-500">Las credenciales se configuran solo en el servidor; nunca se muestran ni se envían al navegador.</p>
      </Panel>
      <PlatformUsagePanel usage={status.usage} webhooks={status.webhooks} />
      <ContactBackfillPanel initial={status.backfill} />
    </div>
  );
};
